import { html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { I18n } from '../../base/package/services/package/i18n/i18n.de';
import { convertClassStringToProps } from '../../base/package/util/classMap';
import { dispatchCustomEvent } from '../../base/package/util/event';
import style from './ui-modal.css?inline';
import '../ui-btn/ui-btn';
import '../ui-icons/ui-close-icon';

export type CallbackFunction = (el: UIModal) => void;

const CName = 'ui-modal';

const rand = () => Math.random().toString(36).slice(2, 8);

/**
 * UI Modal
 *
 * This modal can be closed by dispatching a 'close' event from the content slotted into the 'main' slot.
 *
 * @prop {CallbackFunction} onClosedAction - The callback to execute after the modal close animation finishes.
 * @prop {boolean} isDismissible - Whether to show the close button in the modal. This also determines whether the modal is isDismissible by clicking the backdrop.
 * @prop {CallbackFunction} onAction - The callback to run when the action button is clicked.
 * @prop {CallbackFunction} onAltAction - The callback to run when the alternative action button is clicked.
 * @prop {string} actionButtonLabel - The label for the action button in the modal.
 * @prop {string} altActionButtonLabel - The label for the alternative action button in the modal.
 * @prop {string} class - Additional class names to be added to the component.
 * @fires modal-opened - This event is fired when the modal is opened. Will be caught by the app to control body scrolling.
 * @fires modal-closed - This event is fired when the modal is closed. Will be caught by the app to control body scrolling.
 */
@customElement(CName)
export class UIModal extends LitElement {
  static readonly styles = unsafeCSS(style);
  public static readonly CLOSE_EVENT = 'modal-closed';

  @property() class = '';
  @property() onClosedAction: CallbackFunction;
  @property({ type: Boolean }) isDismissible? = true;
  @property({ type: Boolean }) hideCloseButton? = false;
  @property() onAction?: CallbackFunction;
  @property({ type: Boolean }) actionButtonDisabled = false;
  @property() actionButtonLabel?: string = '';
  @property() actionButtonVariant?: string = '';
  @property() onAltAction?: CallbackFunction;
  @property() altActionButtonLabel?: string = '';
  @property() altActionButtonVariant?: string = '';
  @property({ type: Boolean }) useBodyScrollLock = true;

  private _emptyIconSlot = true;
  private _emptyTitleSlot = true;
  private _closeEventFired = false;
  private _closedByUser = false;

  private readonly _titleA11yId = `${CName}-title-${rand()}`;
  private readonly _descA11yId = `${CName}-desc-${rand()}`;

  @state() private _emptyHeaderSlots = true;
  @state() private _isOpen = true;

  private readonly _animationEndHandler = (ev: AnimationEvent) => {
    if (ev.target !== ev.currentTarget) return;

    window.$app.logger.log('animationend', ev.animationName);
    const mobileCloseAnimation = 'slide-down';
    const desktopCloseAnimation = 'fade-out';
    if (ev.animationName === mobileCloseAnimation || ev.animationName === desktopCloseAnimation) {
      if (typeof this.onClosedAction === 'function') {
        this.onClosedAction(this);
      } else {
        window.$app.logger.log('no `onClosedAction` callback provided');
      }
      this._dispatchCloseEvent();
    }
  };

  private readonly _handleKeyDown = (event: KeyboardEvent) => {
    if (this.isDismissible && event.key === 'Escape') {
      this._userActionClose();
    }
  };

  public open() {
    window.$app.logger.log('open modal');
    this._isOpen = true;
  }

  public close() {
    window.$app.logger.log('UIModal close()');
    this._isOpen = false;
  }

  public get closedByUser() {
    return this._closedByUser;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keydown', this._handleKeyDown);
    if (this.useBodyScrollLock) {
      window.$app.modal.handleBodyScroll('lock', 'modal');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._handleKeyDown);
    if (this.useBodyScrollLock) {
      window.$app.modal.handleBodyScroll('unlock', 'modal');
    }
    window.$app.logger.log('UIModal disconnectedCallback()');
    this._dispatchCloseEvent();
  }

  protected firstUpdated() {
    dispatchCustomEvent(this, 'modal-opened');
  }

  render() {
    const backdropClassList = {
      'modal-backdrop': true,
      'modal-backdrop--closed': !this._isOpen,
      ...convertClassStringToProps(this.class),
    };
    const modalClassList = { modal: true, ...convertClassStringToProps(this.class) };
    const headerClassList = { 'modal-header': true, 'modal-header--empty': this._emptyHeaderSlots };

    return html`
      <div class=${classMap(backdropClassList)} @click=${this._handleBackdropClick}></div>
      <div
        class=${classMap(modalClassList)}
        role="dialog"
        aria-modal="true"
        aria-labelledby=${this._emptyTitleSlot ? nothing : this._titleA11yId}
        aria-describedby=${this._descA11yId}
        @close=${this.close}
        @animationend=${this._animationEndHandler}
      >
        ${this._renderCloseButton()}
        <div class="content-wrapper">
          <div id=${this._titleA11yId} class=${classMap(headerClassList)}>
            <slot
              class="modal-icon"
              name="icon"
              @slotchange=${this._handleHeaderSlotChange}
            ></slot>
            <slot
              class="modal-title"
              name="title"
              @slotchange=${this._handleHeaderSlotChange}
            ></slot>
          </div>
          ${this._renderMainSlot()}
        </div>
        ${
          this._showButtons()
            ? html`<div class="modal-buttons">
                ${
                  this.class.includes('actions-row')
                    ? html`${this._renderAltActionButton()} ${this._renderActionButton()}`
                    : html`${this._renderActionButton()} ${this._renderAltActionButton()}`
                }
              </div>`
            : nothing
        }
        <slot class="modal-footer" name="footer"></slot>
      </div>
    `;
  }

  /**
   * @fires close - This event is fired by content slotted into the 'main' slot.
   */
  private _renderMainSlot() {
    return html`<slot name="main" id=${this._descA11yId} class="modal-main"></slot>`;
  }

  private _renderCloseButton() {
    if (!this.isDismissible || this.hideCloseButton) {
      return nothing;
    }
    return html`
      <button class="close-icon" @click=${this._userActionClose} aria-label=${window.$app.$t.get(I18n.a11y.button.closeDialog)}>
        <ui-close-icon></ui-close-icon>
      </button>
    `;
  }

  private _renderActionButton() {
    if (typeof this.onAction !== 'function' || !this.actionButtonLabel) {
      return nothing;
    }
    return html`
      <ui-btn
        class="modal-button"
        .variant="${this.actionButtonVariant || 'secondary'}"
        .isDisabled=${this.actionButtonDisabled}
        .isBlock=${true}
        data-testid="modalActionButton"
        @click=${() => {
          if (!this.actionButtonDisabled) {
            this.onAction!(this);
          }
        }}
      >
        ${this.actionButtonLabel}
      </ui-btn>
    `;
  }

  private _renderAltActionButton() {
    if (typeof this.onAltAction !== 'function' || !this.altActionButtonLabel) {
      return nothing;
    }
    return html`
      <ui-btn
        class="modal-button"
        variant="${this.altActionButtonVariant || 'secondary-outlined'}"
        .isBlock=${true}
        data-testid="modalAltActionButton"
        @click=${() => this.onAltAction!(this)}
      >
        ${this.altActionButtonLabel}
      </ui-btn>
    `;
  }

  private _userActionClose() {
    this._closedByUser = true;
    this.close();
  }

  private _dispatchCloseEvent() {
    if (!this._closeEventFired) {
      this._closeEventFired = true;
      dispatchCustomEvent(this, UIModal.CLOSE_EVENT);
    }
  }

  private _handleHeaderSlotChange(ev: Event) {
    const slot = ev.target as HTMLSlotElement;
    const hasNodes = slot.assignedNodes().length === 0;
    if (slot.name === 'icon') {
      this._emptyIconSlot = hasNodes;
    } else if (slot.name === 'title') {
      this._emptyTitleSlot = hasNodes;
    }
    this._emptyHeaderSlots = this._emptyIconSlot && this._emptyTitleSlot;
  }

  private _handleBackdropClick() {
    if (!this.isDismissible) {
      return;
    }
    this._userActionClose();
  }

  private _showButtons() {
    return this._renderAltActionButton() !== nothing || this._renderActionButton() !== nothing;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UIModal;
  }
}
